// ForeignDonations.js - Updated with dynamic bank details
import React, { useState, useRef, useEffect } from 'react';
import './ForeignDonations.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// Helper function to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
};

// --- Main ForeignDonations Component ---
export default function ForeignDonations() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    organization: '',
    amount: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [websiteInfo, setWebsiteInfo] = useState(null);

  // Check for logged in user
  useEffect(() => {
    const checkUser = () => {
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          const id = userData.id || userData.user_id || null;
          if (id) {
            setUserId(id);
          }
        }
      } catch (e) {
        console.error('Error checking user:', e);
      }
    };
    checkUser();
  }, []);

  // Fetch website info for bank details
  useEffect(() => {
    const fetchWebsiteInfo = async () => {
      try {
        const result = await apiService.fetchWebinfo();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          setWebsiteInfo(result.data[0]);
        }
      } catch (error) {
        console.error('Error fetching website info:', error);
      }
    };
    fetchWebsiteInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const donationData = {
        user_id: userId || null,
        name: formData.name.trim(),
        email: formData.email.trim(),
        country: formData.country.trim(),
        organization: formData.organization.trim() || null,
        amount: formData.amount.trim() || null,
        message: formData.message.trim() || null,
        status: 'pending'
      };

      const result = await apiService.saveData('foreign_donation_requests', donationData);

      if (result.success) {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your interest! Our team will contact you within 24-48 hours with the FCRA banking details.'
        });
        setFormData({ name: '', email: '', country: '', organization: '', amount: '', message: '' });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: result.message || 'Failed to submit request. Please try again.'
        });
      }
    } catch (error) {
      console.error('❌ Submission error:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Network error: ' + (error.message || 'Please check your connection and try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if bank details exist
  const hasBankDetails = () => {
    if (!websiteInfo) return false;
    return !!(websiteInfo.bank_account_name || websiteInfo.bank_account_number || websiteInfo.bank_name);
  };

  // --- Bank Details Component ---
  const BankDetailsSection = () => {
    if (!hasBankDetails()) return null;

    const bankFields = [
      { key: 'bank_account_name', label: 'Account Holder Name', icon: 'fa-user' },
      { key: 'bank_account_number', label: 'Account Number', icon: 'fa-credit-card' },
      { key: 'bank_account_type', label: 'Account Type', icon: 'fa-layer-group' },
      { key: 'bank_name', label: 'Bank Name', icon: 'fa-university' },
      { key: 'bank_branch', label: 'Branch', icon: 'fa-code-branch' },
      { key: 'bank_address', label: 'Bank Address', icon: 'fa-map-pin' },
      { key: 'bank_ifsc_code', label: 'IFSC Code', icon: 'fa-qrcode' },
      { key: 'bank_rtgs_code', label: 'RTGS/NEFT Code', icon: 'fa-exchange-alt' }
    ];

    const hasAnyBankData = bankFields.some(field => websiteInfo && websiteInfo[field.key]);

    if (!hasAnyBankData) return null;

    return (
      <section className="fcra-details">
        <div className="section-title">
          <span className="eyebrow">Account Information</span>
          <h2>FCRA Account Details</h2>
          <div className="rule" />
        </div>

        <div className="fcra-card-wrapper">
          <div className="fcra-card">
            <div className="fcra-header">
              <div className="fcra-icon">
                <i className="fas fa-university"></i>
              </div>
              <h3>DEVNAR FOUNDATION FOR THE BLIND</h3>
            </div>
            
            <div className="fcra-details-grid">
              {bankFields.map(field => {
                if (!websiteInfo || !websiteInfo[field.key]) return null;
                
                let displayValue = websiteInfo[field.key];
                // Clean HTML for address field
                if (field.key === 'bank_address') {
                  displayValue = stripHtml(displayValue);
                }
                
                return (
                  <div className="fcra-detail-item" key={field.key}>
                    <span className="detail-label">
                      <i className={`fas ${field.icon}`}></i> {field.label}
                    </span>
                    <span className="detail-value">{displayValue}</span>
                  </div>
                );
              })}
            </div>

            <div className="fcra-contact">
              <p>
                <i className="fas fa-envelope"></i>
                For complete banking and compliance details, please contact us at:
              </p>
              <a href={`mailto:${websiteInfo?.contact_email || 'info@devnarfoundation.org'}`} className="contact-email">
                {websiteInfo?.contact_email || 'info@devnarfoundation.org'}
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // --- DONATION_IMPACT ---
  const DONATION_IMPACT = [
    {
      icon: 'fa-globe-asia',
      title: 'Global Support',
      description: 'International donors from over 14 countries have supported our mission.'
    },
    {
      icon: 'fa-file-alt',
      title: 'FCRA Compliant',
      description: 'Fully compliant with the Foreign Contribution Regulation Act (FCRA).'
    },
    {
      icon: 'fa-handshake',
      title: 'Transparent Giving',
      description: 'Complete transparency with official receipts and compliance documentation.'
    },
    {
      icon: 'fa-chart-line',
      title: 'Proven Impact',
      description: 'Your contribution directly supports education, boarding, and medical care for over 500 students.'
    }
  ];

  // --- DONATION_PROCESS ---
  const DONATION_PROCESS = [
    {
      step: 1,
      title: 'Contact Us',
      description: 'Reach out to us via email or the form below expressing your interest in making a foreign donation.'
    },
    {
      step: 2,
      title: 'Receive Account Details',
      description: 'We will share the complete FCRA banking details and compliance documentation with you.'
    },
    {
      step: 3,
      title: 'Make Your Donation',
      description: 'Transfer your contribution to our FCRA account. All donations are 100% compliant with Indian regulations.'
    },
    {
      step: 4,
      title: 'Receive Acknowledgment',
      description: 'You will receive an official acknowledgment receipt for your donation, compliant with FCRA requirements.'
    }
  ];

  // --- ADDITIONAL_INFO ---
  const ADDITIONAL_INFO = [
    {
      icon: 'fa-scroll',
      title: 'FCRA Registration',
      description: 'Devnar Foundation holds valid FCRA registration from the Ministry of Home Affairs, Government of India.',
      tag: `Registration No: 010230188`
    },
    {
      icon: 'fa-shield-alt',
      title: '100% Transparency',
      description: 'We maintain complete transparency in all foreign donations, with proper documentation and reporting as per FCRA guidelines.'
    },
    {
      icon: 'fa-globe',
      title: 'Global Impact',
      description: 'International donors from over 14 countries have supported our mission, contributing to the education and empowerment of visually challenged children.'
    },
    {
      icon: 'fa-receipt',
      title: 'Official Receipt',
      description: 'All foreign donors receive an official acknowledgment receipt that can be used for tax purposes in their respective countries.'
    }
  ];

  // --- Reusable Components ---
  function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
        { threshold: 0.15 }
      );
      obs.observe(el);
      return () => obs.disconnect();
    }, [ref]);
    return (
      <Tag
        ref={ref}
        className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
        style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
        {...rest}
      >
        {children}
      </Tag>
    );
  }

  return (
    <>
      <Header />
      <div className="foreign-donations-page devnar">
        <a className="skip-link" href="#main-foreign">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-foreign">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-foreign">
            <Reveal>
              <span className="hero-badge">International Giving</span>
              <h1>Foreign Donations</h1>
              <p className="hero-subtitle">
                FCRA-compliant international giving to support the education and empowerment 
                of visually challenged children in India.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-foreign" className="wrap page-content-foreign">

          {/* --- Introduction --- */}
          <section className="foreign-intro">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">FCRA Compliant</span>
              <h2>Support from Around the World</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Devnar Foundation for the Blind holds FCRA accreditation from the Ministry of Home Affairs, 
                Government of India. This enables us to receive foreign contributions in full compliance 
                with the Foreign Contribution Regulation Act.
              </p>
            </Reveal>
            
            <div className="intro-cards">
              {DONATION_IMPACT.map((item, index) => (
                <Reveal key={item.title} className="intro-card" delay={index * 60}>
                  <div className="intro-card-icon">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Dynamic Bank Details Section --- */}
          <BankDetailsSection />

          {/* --- Donation Process --- */}
          <section className="donation-process">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">How It Works</span>
              <h2>Process for Foreign Donations</h2>
              <div className="rule" />
            </Reveal>

            <div className="process-steps">
              {DONATION_PROCESS.map((step, index) => (
                <Reveal key={step.step} className="process-step" delay={index * 80}>
                  <div className="step-number">{step.step}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Additional Information --- */}
          <section className="additional-info">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Important Information</span>
              <h2>FCRA Compliance & Transparency</h2>
              <div className="rule" />
            </Reveal>

            <div className="info-grid">
              {ADDITIONAL_INFO.map((item, index) => (
                <Reveal key={item.title} className="info-card" delay={index * 80}>
                  <div className="info-icon">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  {item.tag && <span className="info-tag">{item.tag}</span>}
                </Reveal>
              ))}
            </div>
          </section>

        </main>
      </div>
      <Footer />
    </>
  );
}